<template>
  <div class="invoice-view-wrapper">
    <!-- ===== HEADER ===== -->
    <div class="invoice-header">
      <div class="company-info">
        <img v-if="invoice.logo" :src="invoice.logo" class="company-logo" alt="Company Logo" />
        <div v-else class="company-logo-fallback">AI</div>
        <div class="company-details">
          <h3 class="company-name">{{ invoice.company.name }}</h3>
          <p>{{ invoice.company.addressLine1 }}</p>
          <p>{{ invoice.company.city }}, {{ invoice.company.state }} - {{ invoice.company.pincode }}</p>
          <p>{{ invoice.company.country }}</p>
          <p>{{ invoice.company.mobile }}</p>
        </div>
      </div>

      <div class="invoice-meta">
        <h2 class="invoice-number">Invoice #{{ invoice.ref }}</h2>
        <div class="meta-details">
          <p><strong>Date:</strong> {{ invoice.date }}</p>
          <p><strong>Due Date:</strong> {{ invoice.dueDate }}</p>
          <p>
            <strong>Status:</strong>
            &nbsp;
            <span :class="['status', invoice.status.toLowerCase()]">{{ invoice.status }}</span>
          </p>
        </div>
      </div>
    </div>

    <hr class="divider" />

    <!-- ===== BILL TO ===== -->
    <div class="billto-section">
      <div class="client-info">
        <h4 class="section-title">Invoice To</h4>
        <p class="client-name">{{ invoice.client.name }}</p>
        <p>{{ invoice.client.address }}</p>
        <p>{{ invoice.client.city }}, {{ invoice.client.state }}</p>
        <p>{{ invoice.client.country }} - {{ invoice.client.pincode }}</p>
        <p>{{ invoice.client.email }}</p>
      </div>
      <div class="amount-due">
        <h4 class="section-title">Amount Due</h4>
        <h2 class="due-value">$ {{ invoice.total }}</h2>
      </div>
    </div>

    <!-- ===== ITEMS TABLE ===== -->
    <div class="invoice-items">
      <table>
        <thead>
          <tr>
            <th>#</th>
            <th>Item</th>
            <th>Rate</th>
            <th>Qty</th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, i) in invoice.items" :key="i">
            <td>{{ i + 1 }}</td>
            <td>{{ item.title }}</td>
            <td>$ {{ item.rate }}</td>
            <td>{{ item.qty }}</td>
            <td>$ {{ item.total }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- ===== TOTALS ===== -->
    <div class="invoice-totals">
      <div class="totals-card">
        <div class="total-line">
          <span>Subtotal</span>
          <span>$ {{ invoice.subtotal }}</span>
        </div>
        <div class="total-line">
          <span>Discount</span>
          <span>- $ {{ invoice.discount }}</span>
        </div>
        <div class="total-line">
          <span>Additional Charge</span>
          <span>+ $ {{ invoice.addCharge }}</span>
        </div>
        <div class="total-line">
          <span>Tax</span>
          <span>$ {{ invoice.tax }}</span>
        </div>
        <hr />
        <div class="total-line grand">
          <strong>Total</strong>
          <strong>$ {{ invoice.total }}</strong>
        </div>
      </div>
    </div>

    <!-- ===== NOTES & SIGNATURE ===== -->
    <div class="invoice-notes">
      <div>
        <h4>Terms</h4>
        <p>{{ invoice.terms }}</p>
      </div>
      <div>
        <h4>Notes</h4>
        <p>{{ invoice.notes }}</p>
      </div>
      <div v-if="invoice.signature" class="signature-block">
        <img :src="invoice.signature" alt="Signature" class="signature" />
        <p>Authorized Signature</p>
      </div>
    </div>

    <!-- ===== ACTIONS ===== -->
    <div class="invoice-actions">
      <button class="btn-primary">
        <i class="bx bx-paper-plane"></i>&nbsp;Send Invoice
      </button>
      <button class="btn-secondary"><i class="bx bx-printer"></i> Print</button>
      <button class="btn-secondary"><i class="bx bx-download"></i> Download</button>
    </div>
  </div>
</template>

<script setup>
const invoice = {
  logo: "",
  ref: "INV-2098",
  date: "2025-11-03",
  dueDate: "2025-11-10",
  status: "Pending",
  company: {
    name: "AI Business Co.",
    addressLine1: "45 Innovation Avenue",
    city: "San Francisco",
    state: "CA",
    country: "USA",
    pincode: "94105",
    mobile: "+1 800 200 4000",
  },
  client: {
    name: "TechSpark Ltd",
    address: "22 King Street",
    city: "London",
    state: "UK",
    country: "United Kingdom",
    pincode: "W1B 5AN",
    email: "billing@techspark.com",
  },
  items: [
    { title: "AI Automation Setup", rate: 1200, qty: 1, total: 1200 },
    { title: "Support Subscription", rate: 250, qty: 2, total: 500 },
  ],
  subtotal: 1700,
  discount: 100,
  addCharge: 50,
  tax: 85,
  total: 1735,
  currency: "$",
  terms: "Payment due within 7 days.",
  notes: "Thank you for your business!",
  signature: "https://upload.wikimedia.org/wikipedia/commons/1/1b/Signature_example.png",
};
</script>

<style scoped>
.invoice-view-wrapper {
  background: #fff;
  border-radius: 1.2rem;
  padding: 2.8rem 3.5rem;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.05);
  font-family: "Inter", sans-serif;
  animation: fadeIn 0.4s ease;
}

.invoice-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 2.5rem;
}

.company-info {
  display: flex;
  align-items: flex-start;
  gap: 1.2rem;
}
.company-logo {
  width: 80px;
  height: 80px;
  border-radius: 12px;
  object-fit: cover;
}
.company-logo-fallback {
  width: 3rem;
  height: 3rem;
  border-radius: 12px;
  background: #111827;
  color: #fff;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
}
.company-name {
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.4rem;
}

.invoice-meta {
  text-align: right;
}
.invoice-number {
  font-size: 1.6rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.6rem;
}
.meta-details p {
  margin: 0.4rem 0;
  color: #4b5563;
  font-size: 0.95rem;
}

.status {
  padding: 0.3rem 0.7rem;
  border-radius: 0.5rem;
  color: #fff;
  font-size: 0.85rem;
}
.status.pending {
  background-color: #f59e0b;
}
.status.paid {
  background-color: #16a34a;
}
.status.overdue {
  background-color: #dc2626;
}

.divider {
  border: none;
  border-top: 1px solid #e5e7eb;
  margin: 2rem 0;
}

.billto-section {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  margin-bottom: 2.2rem;
  gap: 2rem;
}
.section-title {
  color: #111827;
  font-weight: 600;
  margin-bottom: 0.8rem;
}
.client-name {
  font-weight: 500;
  color: #111827;
}
.due-value {
  font-size: 2rem;
  font-weight: 700;
  color: #111827;
}

.invoice-items table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 2.5rem;
}
.invoice-items th,
.invoice-items td {
  padding: 1rem 1rem;
  border-bottom: 1px solid #e5e7eb;
  font-size: 0.95rem;
}
.invoice-items th {
  background-color: #f9fafb;
  font-weight: 600;
  color: #111827;
}

.invoice-totals {
  display: flex;
  justify-content: flex-end;
  margin-top: 1.5rem;
}
.totals-card {
  width: 340px;
  background: #f9fafb;
  border-radius: 14px;
  padding: 1.6rem 1.8rem;
}
.total-line {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.6rem;
}
.total-line.grand {
  font-weight: 700;
  border-top: 1px solid #e5e7eb;
  padding-top: 0.7rem;
}

.invoice-notes {
  display: flex;
  justify-content: space-between;
  margin-top: 3rem;
  flex-wrap: wrap;
  gap: 2rem;
}
.invoice-notes h4 {
  margin-bottom: 0.4rem;
}
.invoice-notes p {
  line-height: 1.6;
  color: #4b5563;
}
.signature-block {
  text-align: center;
}
.signature {
  width: 130px;
  margin-bottom: 0.4rem;
}

.invoice-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 3rem;
}
.btn-primary {
  background: #111827;
  color: #fff;
  padding: 0.9rem 1.8rem;
  border: none;
  border-radius: 10px;
  font-weight: 500;
  cursor: pointer;
  transition: 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}
.btn-primary:hover {
  background: #1f2937;
}
.btn-secondary {
  background: #f3f4f6;
  border: none;
  padding: 0.9rem 1.6rem;
  border-radius: 10px;
  color: #111827;
  font-weight: 500;
  cursor: pointer;
  transition: 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}
.btn-secondary:hover {
  background: #e5e7eb;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
